import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { getNeedsInfoClaimUi } from "@/lib/claim-status";
import { submitNursingHomeClaimReply } from "@/lib/nursing-home-claim-reply";
import { toast } from "sonner";

interface NeedsInfoClaim {
  id: string;
  nursing_home_id: string;
  status: string;
  created_at: string;
  updated_at: string;
  notes?: string | null;
  nursing_home?: {
    center_name: string;
    town: string;
    county: string;
    county_slug: string;
    nh_slug: string;
  };
}

interface NeedsInfoClaimsProps {
  claims: NeedsInfoClaim[];
  isLoading: boolean;
  onClaimsUpdated?: () => void | Promise<void>;
}

export function NeedsInfoClaims({ claims, isLoading, onClaimsUpdated }: NeedsInfoClaimsProps) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  if (isLoading || !claims || claims.length === 0) {
    return null;
  }

  const handleSubmit = async (claimId: string) => {
    const reply = (drafts[claimId] ?? "").trim();
    if (reply.length < 3) {
      toast.error("Please add a short response (at least a few characters).");
      return;
    }
    setSubmittingId(claimId);
    try {
      await submitNursingHomeClaimReply(claimId, reply);
      toast.success("Thanks — your claim is back in our review queue.");
      setDrafts((d) => ({ ...d, [claimId]: "" }));

      await onClaimsUpdated?.();
    } catch (e) {
      console.error(e);
      const msg = e instanceof Error ? e.message : "Could not submit your reply.";
      toast.error(msg);
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <Card className="mb-6 border-orange-200 bg-orange-50/40">
      <CardHeader className="border-b border-orange-100">
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-600" />
          Action required
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Grandpal needs more information to verify these claims. Reply below and we&apos;ll continue the review.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-orange-100">
          {claims.map((claim) => {
            const ui = getNeedsInfoClaimUi(claim.notes);
            const busy = submittingId === claim.id;
            return (
              <div key={claim.id} className="p-4 space-y-4">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">
                      {claim.nursing_home?.center_name || "Nursing home"}
                    </h3>
                    {claim.nursing_home && (
                      <p className="text-sm text-muted-foreground mb-2">
                        {claim.nursing_home.town}, {claim.nursing_home.county}
                      </p>
                    )}
                  </div>
                  <Badge className="bg-orange-100 text-orange-900" variant="secondary">
                    {ui.label}
                  </Badge>
                </div>
                <div className="rounded-lg border border-orange-200 bg-white p-3 text-sm text-foreground">
                  <span className="font-medium text-muted-foreground block mb-1">From our team</span>
                  {claim.notes?.trim() ? claim.notes : ui.description}
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`reply-${claim.id}`}>Your response</Label>
                  <Textarea
                    id={`reply-${claim.id}`}
                    placeholder="Provide the information requested (e.g. registration details, letter of authority, or how we can reach you)."
                    rows={4}
                    disabled={busy}
                    value={drafts[claim.id] ?? ""}
                    onChange={(e) =>
                      setDrafts((d) => ({ ...d, [claim.id]: e.target.value }))
                    }
                  />
                  <Button
                    type="button"
                    disabled={busy}
                    className="bg-grandpal-green-dark hover:bg-grandpal-green-darkHover"
                    onClick={() => handleSubmit(claim.id)}
                  >
                    {busy ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      "Submit response & continue review"
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
